// Pure helpers for ProductionSetupWizard — no React dependency, easy to unit-test.

// Prefix for every wizard draft key. Scoped per-tenant + per-user so
// switching accounts, impersonating tenants, or logging out never exposes
// another user's in-progress draft. The bare prefix (without a suffix) is
// also used by authStore.logout to sweep every draft for the current browser.
export const DRAFT_STORAGE_PREFIX = 'production-setup-wizard-draft'
// Back-compat constant — older code / tests still read this name.
export const DRAFT_STORAGE_KEY = DRAFT_STORAGE_PREFIX

export function draftStorageKey(user, tenant) {
  const tenantId = tenant?.id ?? user?.tenant_id ?? null
  const userId = user?.id ?? null
  if (tenantId == null || userId == null) {
    return `${DRAFT_STORAGE_PREFIX}:anon`
  }
  return `${DRAFT_STORAGE_PREFIX}:${tenantId}:${userId}`
}
export const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
// JS Date.getUTCDay() returns 0 (Sun) .. 6 (Sat); map to our Mon-first day keys.
export const JS_DAY_TO_KEY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
export const PHASES = ['all', 'planning', 'load_in', 'show', 'load_out']
export const STEP_KEYS = ['wizardStepVenue', 'wizardStepDates', 'wizardStepTeam', 'wizardStepRehearsals']

export function addDaysISO(isoDate, days) {
  if (!isoDate) return ''
  const d = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return ''
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

export function differenceInDaysISO(a, b) {
  if (!a || !b) return 0
  const da = new Date(`${a}T00:00:00Z`).getTime()
  const db = new Date(`${b}T00:00:00Z`).getTime()
  if (Number.isNaN(da) || Number.isNaN(db)) return 0
  return Math.round((db - da) / 86_400_000)
}

// Pure helper: generate rehearsal entries from recurring pattern config.
export function generateRehearsalEntries(pattern) {
  const { fromDate, toDate, daysOfWeek, startTime, endTime, title } = pattern
  if (!fromDate || !toDate) return []
  if (toDate < fromDate) return []
  const out = []
  let cursor = fromDate
  let safety = 0
  while (cursor && cursor <= toDate && safety < 3000) {
    safety += 1
    const d = new Date(`${cursor}T00:00:00Z`)
    const dayKey = JS_DAY_TO_KEY[d.getUTCDay()]
    if (daysOfWeek.includes(dayKey)) {
      out.push({
        entry_date: cursor,
        title: title || 'Rehearsal',
        start_time: startTime || null,
        end_time: endTime || null,
        entry_type: 'rehearsal',
        location: null,
        plan: null,
        resources: null,
        notes: null,
        status: 'planned',
        sort_order: out.length,
      })
    }
    cursor = addDaysISO(cursor, 1)
  }
  return out
}

export function emptyDraft() {
  return {
    step: 0,
    venue: {
      name: '',
      description: '',
      location_id: '',
      customVenue: '',
      useCustomVenue: false,
    },
    dates: {
      show_start: '',
      show_end: '',
      planning_start: '',
      load_in_start: '',
      load_in_end: '',
      load_out_start: '',
      load_out_end: '',
    },
    team: [],
    rehearsals: {
      mode: 'skip',
      pattern: {
        fromDate: '',
        toDate: '',
        daysOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri'],
        startTime: '18:00',
        endTime: '22:00',
        title: '',
      },
    },
  }
}

function resolveStorage(storage) {
  if (storage) return storage
  return typeof window !== 'undefined' ? window.sessionStorage : null
}

export function readDraft(key = DRAFT_STORAGE_KEY, storage = undefined) {
  try {
    const s = resolveStorage(storage)
    const raw = s?.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch {
    return null
  }
}

export function writeDraft(draft, key = DRAFT_STORAGE_KEY, storage = undefined) {
  try {
    const s = resolveStorage(storage)
    s?.setItem(key, JSON.stringify(draft))
  } catch { /* quota or disabled */ }
}

export function clearDraft(key = DRAFT_STORAGE_KEY, storage = undefined) {
  try {
    const s = resolveStorage(storage)
    s?.removeItem(key)
  } catch { /* ignore */ }
}

// Sweep every namespaced wizard draft. Used by authStore.logout so no
// draft survives a logout regardless of which tenant/user it belongs to.
export function clearAllWizardDrafts(storage = undefined) {
  try {
    const s = resolveStorage(storage)
    if (!s) return
    const keys = []
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i)
      if (k && k.startsWith(DRAFT_STORAGE_PREFIX)) keys.push(k)
    }
    keys.forEach(k => s.removeItem(k))
  } catch { /* ignore */ }
}

export function hasDraftData(d) {
  if (!d) return false
  if (d.venue?.name || d.venue?.description || d.venue?.location_id || d.venue?.customVenue) return true
  const dk = d.dates || {}
  if (dk.show_start || dk.show_end || dk.planning_start || dk.load_in_start ||
      dk.load_in_end || dk.load_out_start || dk.load_out_end) return true
  if (Array.isArray(d.team) && d.team.length > 0) return true
  if (d.rehearsals?.mode === 'recurring') return true
  return false
}

export function buildProductionPayload(draft) {
  const v = draft.venue
  const d = draft.dates
  const venueText = v.useCustomVenue ? (v.customVenue || '').trim() : ''
  return {
    name: (v.name || '').trim(),
    description: v.description || '',
    status: 'planned',
    location_id: !v.useCustomVenue && v.location_id ? v.location_id : null,
    venue: venueText || null,
    planning_start: d.planning_start || null,
    load_in_start: d.load_in_start || null,
    load_in_end: d.load_in_end || null,
    show_start: d.show_start,
    show_end: d.show_end,
    load_out_start: d.load_out_start || null,
    load_out_end: d.load_out_end || null,
  }
}

export function phaseKey(phase) {
  switch (phase) {
    case 'all': return 'wizardPhaseAll'
    case 'planning': return 'wizardPhasePlanning'
    case 'load_in': return 'wizardPhaseLoadIn'
    case 'show': return 'wizardPhaseShow'
    case 'load_out': return 'wizardPhaseLoadOut'
    default: return 'wizardPhaseAll'
  }
}
